import { createContext, FC, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deleteAppsInRole,
  getAppsInRole,
  postAppsInRole,
} from "../../api/broker";
import { useAuthContext } from "../../hooks/Auth";
import { iDataComponentes } from "../../interfaces/SuperAdmin/Others/iDataComponentes";
import { iDataRolePermissions } from "../../interfaces/SuperAdmin/Pages/iDataRolePermissions";

interface PermissionContextProps {
  isLoadingPermissions: boolean;
  permissions: iDataRolePermissions[];
  selectedPermissions: string[];
  components: iDataComponentes[];
  isModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isAdding: boolean;
  missingComponents: iDataComponentes[];
  isDeleting: boolean;
  openModal: () => void;
  closeModal: () => void;
  submitSelections: (body: {
    id_rol: string;
    id_componentes: string[];
  }) => void;
  setSelectedPermissions: React.Dispatch<React.SetStateAction<string[]>>;
  toggleDeleteModal: (isOpen: boolean) => void;
  deleteSelections: (body: { ids: string[] }) => Promise<void>;
}

const PermissionContext = createContext<PermissionContextProps>(
  {} as PermissionContextProps
);

interface Props {
  children: React.ReactNode;
  isPermissionsTab: 0 | 1;
}

const PermissionProvider: FC<Props> = ({ children, isPermissionsTab }) => {
  const { idRol } = useParams();
  const { user } = useAuthContext();
  const [permissions, setPermissions] = useState<iDataRolePermissions[]>([]);
  const [components, setComponents] = useState<iDataComponentes[]>([]); // Temporalmente se sacarán del api
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [missingComponents, setMissingComponents] = useState<
    iDataComponentes[]
  >([]);

  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Extraer los permisos del rol
  const fetchPermissions = async (id: string | undefined) => {
    if (id) {
      setIsLoadingPermissions(true);
      try {
        const res = await getAppsInRole(id);
        console.log(res);
        if (res.data.statusCode === 200) {
          setPermissions(res.data.body);
        } else {
          toast.error("Error al obtener las apps del rol");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener las apps del rol");
      } finally {
        setIsLoadingPermissions(false);
      }
    }
  };

  useEffect(() => {
    if (isPermissionsTab === 0) {
      fetchPermissions(idRol);
    }
  }, [isPermissionsTab]);

  useEffect(() => {
    if (user) {
      setComponents(user.ummiapps);
    }
  }, [user, user?.ummiapps]);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const toggleDeleteModal = (isOpen: boolean) => {
    setIsDeleteModalOpen(isOpen);
  };

  useEffect(() => {
    // Hacemos un filtro de todos los componentes. Evaluamos si alguno de los permisos del rol ya incluye ese componente.
    //De esta forma dejamos solo los componentes que no han sido elegidos
    const newArray = components.filter(
      (comp) =>
        !permissions.some(
          (permission) => permission.id_componente === comp.id_componente
        )
    );
    setMissingComponents(newArray);
  }, [permissions, components]);

  // Crear permisos para componentes
  const submitSelections = async (body: {
    id_rol: string;
    id_componentes: string[];
  }) => {
    setIsAdding(true);
    try {
      const res = await postAppsInRole(body);
      console.log(res);
      if (res.data.statusCode === 201) {
        setPermissions((prev) => {
          return [...prev, ...res.data.body];
        });
        closeModal();
        toast.success("Apps agregadas al rol exitosamente");
      } else {
        toast.error("Error al agregar las apps al rol");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al agregar las apps al rol");
    } finally {
      setIsAdding(false);
    }
  };

  // Borrar permisos para componentes
  const deleteSelections = async (body: { ids: string[] }) => {
    setIsDeleting(true);
    try {
      const res = await deleteAppsInRole(body);
      console.log(res);
      if (res.data.statusCode === 200) {
        setSelectedPermissions([]);
        setPermissions((prev) =>
          prev.filter((item) => !body.ids.includes(item.id_rol_permiso_comp))
        );
        toggleDeleteModal(false);
        toast.success("Apps eliminadas del rol exitosamente");
      } else {
        toast.error("Error al eliminar las apps del rol");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar las apps del rol");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        isLoadingPermissions,
        isModalOpen,
        isDeleteModalOpen,
        isAdding,
        components,
        missingComponents,
        selectedPermissions,
        isDeleting,
        openModal,
        closeModal,
        submitSelections,
        setSelectedPermissions,
        toggleDeleteModal,
        deleteSelections,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

const useSuperAdminPermissions = () => useContext(PermissionContext);

export { PermissionProvider, useSuperAdminPermissions };
